// @ts-check
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  selectLocalPeerRoleName,
  selectPermissions,
  selectPolls,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { QuestionIcon, StatsIcon } from "@100mslive/react-icons";
import { Button, Flex, Input, Switch, Text } from "@100mslive/roomkit-react";
import { Container, ContentHeader, ErrorText } from "../../Streaming/Common";
import { useWidgetToggle } from "../../AppData/useSidepane";
import { useWidgetState } from "../../AppData/useUISettings";
import { isValidTextInput } from "../../../common/utils";
import { StatusIndicator } from "../common/StatusIndicator";
import {
  INTERACTION_TYPE,
  WIDGET_STATE,
  WIDGET_VIEWS,
} from "../../../common/constants";
import {
  getTemplates,
  saveTemplate,
  deleteTemplate,
  exportTemplates,
  importTemplates,
  importTemplatesFromUrl,
} from "../utils/templateStorage";

export const PollsQuizMenu = () => {
  const toggleWidget = useWidgetToggle();
  const { setWidgetView } = useWidgetState();
  const permissions = useHMSStore(selectPermissions);

  return (
    <Container rounded>
      <ContentHeader
        content="Polls/Quiz"
        onBack={() => setWidgetView(WIDGET_VIEWS.LANDING)}
        onClose={toggleWidget}
      />
      <Flex
        direction="column"
        css={{ px: "$10", pb: "$10", overflowY: "auto" }}
      >
        {permissions?.pollWrite && <AddMenu />}
        {permissions?.pollWrite && <SavedTemplatesMenu />}
        <PrevMenu />
      </Flex>
    </Container>
  );
};

function InteractionSelectionCard({ title, icon, active, onClick }) {
  const activeBorderStyle = active
    ? "$space$px solid $primary_default"
    : "$space$px solid $border_bright";
  return (
    <Flex
      onClick={onClick}
      css={{
        border: activeBorderStyle,
        p: "$4",
        r: "$2",
        w: "100%",
        cursor: "pointer",
      }}
      align="center"
    >
      <Flex
        css={{
          border: activeBorderStyle,
          p: "$4",
          bg: "$surface_bright",
          c: "$on_surface_high",
          r: "$0",
        }}
      >
        {icon}
      </Flex>
      <Text variant="sub1" css={{ ml: "$md", color: "#191B23" }}>
        {title}
      </Text>
    </Flex>
  );
}

const AddMenu = () => {
  const actions = useHMSActions();
  const [title, setTitle] = useState("");
  const localPeerRoleName = useHMSStore(selectLocalPeerRoleName);
  const [anonymous, setAnonymous] = useState(false);
  const [hideVoteCount, setHideVoteCount] = useState(false);
  const [error, setError] = useState();
  const [titleError, setTitleError] = useState("");
  const { setWidgetState } = useWidgetState();
  const [interactionType, setInteractionType] = useState(INTERACTION_TYPE.POLL);

  const handleCreate = id => {
    setWidgetState({
      [WIDGET_STATE.pollInView]: id,
      [WIDGET_STATE.view]: WIDGET_VIEWS.CREATE_QUESTIONS,
    });
  };

  const validateTitle = useMemo(() => {
    if (!isValidTextInput(title)) {
      if (title) {
        setTitleError("Title of Poll/Quiz need to between 2-100 characters");
      }
      return true;
    } else {
      setTitleError("");
      return false;
    }
  }, [title]);
  // const [timer, setTimer] = useState(10);
  // const [showTimerDropDown, setShowTimerDropDown] = useState(false);

  return (
    <>
      <Text variant="caption" css={{ color: "#2E3038", mb: "$md" }}>
        Select the type you want to continue with
      </Text>
      <Flex css={{ w: "100%", gap: "$10", mb: "$md" }}>
        <InteractionSelectionCard
          title={INTERACTION_TYPE.POLL}
          icon={<StatsIcon width={32} height={32} />}
          onClick={() => setInteractionType(INTERACTION_TYPE.POLL)}
          active={interactionType === INTERACTION_TYPE.POLL}
        />
        <InteractionSelectionCard
          title={INTERACTION_TYPE.QUIZ}
          icon={<QuestionIcon width={32} height={32} />}
          onClick={() => setInteractionType(INTERACTION_TYPE.QUIZ)}
          active={interactionType === INTERACTION_TYPE.QUIZ}
        />
      </Flex>
      <Flex direction="column">
        <Text
          variant="body2"
          css={{ mb: "$4", color: "#191B23" }}
        >{`Name this ${interactionType.toLowerCase()}`}</Text>
        <Input
          type="text"
          value={title}
          onChange={event => setTitle(event.target.value)}
          css={{
            backgroundColor: "$surface_bright",
            border: "1px solid $border_default",
          }}
        />
        <Flex align="center" css={{ mt: "$10" }}>
          <Switch
            onCheckedChange={value => setHideVoteCount(value)}
            css={{ mr: "$6" }}
          />
          <Text variant="body2" css={{ color: "#2E3038" }}>
            Hide Vote Count
          </Text>
        </Flex>
        <Flex align="center" css={{ mt: "$10" }}>
          <Switch
            onCheckedChange={value => setAnonymous(value)}
            css={{ mr: "$6" }}
          />
          <Text variant="body2" css={{ color: "#2E3038" }}>
            Make Results Anonymous
          </Text>
        </Flex>
        {/* <Timer
        timer={timer}
        setTimer={setTimer}
        showTimerDropDown={showTimerDropDown}
        setShowTimerDropDown={setShowTimerDropDown}
      /> */}

        <Button
          variant="primary"
          disabled={validateTitle}
          css={{ mt: "$10" }}
          onClick={async () => {
            const id = Date.now().toString();
            await actions.interactivityCenter
              .createPoll({
                id,
                title,
                anonymous,
                rolesThatCanViewResponses:
                  hideVoteCount && localPeerRoleName
                    ? [localPeerRoleName]
                    : undefined,
                type: interactionType.toLowerCase(),
                // duration: showTimerDropDown ? timer : undefined,
              })
              .then(() => handleCreate(id))
              .catch(err => setError(err.message));
          }}
        >
          Create {interactionType}
        </Button>
        <ErrorText error={error || titleError} />
      </Flex>
    </>
  );
};

const PrevMenu = () => {
  const polls = useHMSStore(selectPolls)?.filter(
    poll => poll.state === "started" || poll.state === "stopped"
  );
  return polls?.length ? (
    <Flex
      css={{
        borderTop: "$space$px solid $border_bright",
        mt: "$10",
        pt: "$10",
      }}
    >
      <Flex direction="column" css={{ w: "100%" }}>
        <Text variant="h6" css={{ color: "#191B23" }}>
          Previous Polls/Quiz
        </Text>
        <Flex direction="column" css={{ gap: "$10", mt: "$8" }}>
          {polls.map(poll => (
            <InteractionCard
              key={poll.id}
              id={poll.id}
              title={poll.title}
              isLive={poll.state === "started"}
              isTimed={(poll.duration || 0) > 0}
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  ) : null;
};

const InteractionCard = ({ id, title, isLive, isTimed }) => {
  const { setWidgetState } = useWidgetState();

  const goToVote = id => {
    setWidgetState({
      [WIDGET_STATE.pollInView]: id,
      [WIDGET_STATE.view]: WIDGET_VIEWS.VOTE,
    });
  };

  return (
    <Flex
      direction="column"
      css={{ backgroundColor: "$surface_bright", borderRadius: "$1", p: "$8" }}
    >
      <Flex css={{ w: "100%", justifyContent: "space-between", mb: "$sm" }}>
        <Text
          variant="sub1"
          css={{ color: "#191B23", fontWeight: "$semiBold" }}
        >
          {title}
        </Text>
        <StatusIndicator isLive={isLive} shouldShowTimer={isLive && isTimed} />
      </Flex>
      <Flex css={{ w: "100%", gap: "$4" }} justify="end">
        <Button variant="primary" onClick={() => goToVote(id)}>
          View
        </Button>
      </Flex>
    </Flex>
  );
};

const SavedTemplatesMenu = () => {
  const [templates, setTemplates] = useState([]);
  const [importError, setImportError] = useState("");
  const [importMode, setImportMode] = useState("file"); // "file" or "link"
  const [linkUrl, setLinkUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = () => {
    const savedTemplates = getTemplates();
    setTemplates(savedTemplates);
  };

  const handleExport = async () => {
    try {
      // Check if there are templates before attempting export
      if (templates.length === 0) {
        setImportError("No templates available to export. Please save a template first.");
        return;
      }
      
      setImportError("");
      exportTemplates();
      // Clear any previous errors on success
      setTimeout(() => setImportError(""), 100);
    } catch (error) {
      console.error("Export error:", error);
      setImportError("Failed to export templates: " + error.message);
    }
  };

  const handleFileSelect = event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      setImportError("Please select a valid JSON file");
      return;
    }

    setIsImporting(true);
    importTemplates(file)
      .then(() => {
        loadTemplates();
        setImportError("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      })
      .catch(error => {
        setImportError("Failed to import templates: " + error.message);
      })
      .finally(() => {
        setIsImporting(false);
      });
  };

  const handleImportFromLink = async () => {
    if (!linkUrl.trim()) {
      setImportError("Please enter a valid URL");
      return;
    }

    setIsImporting(true);
    setImportError("");
    try {
      await importTemplatesFromUrl(linkUrl.trim());
      loadTemplates();
      setLinkUrl("");
      setImportError("");
    } catch (error) {
      setImportError(error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleDeleteTemplate = templateId => {
    try {
      deleteTemplate(templateId);
      loadTemplates();
      setImportError("");
    } catch (error) {
      setImportError("Failed to delete template: " + error.message);
    }
  };

  return (
    <Flex
      css={{
        borderTop: "$space$px solid $border_bright",
        mt: "$10",
        pt: "$10",
      }}
      direction="column"
    >
      <Flex
        css={{ w: "100%", justifyContent: "space-between", alignItems: "center", mb: "$8" }}
      >
        <Text variant="h6" css={{ color: "#191B23", fontWeight: "$semiBold" }}>
          Saved Templates
        </Text>
        <Button
          variant="standard"
          onClick={handleExport}
          disabled={templates.length === 0}
          title={templates.length === 0 ? "No templates to export" : "Export all saved templates"}
        >
          Export
        </Button>
      </Flex>

      <Flex direction="column" css={{ mb: "$8" }}>
        <Text variant="body2" css={{ mb: "$4", color: "#2E3038" }}>
          Import Templates:
        </Text>
        <Flex css={{ gap: "$4", mb: "$4" }}>
          <Button
            variant={importMode === "file" ? "primary" : "standard"}
            onClick={() => {
              setImportMode("file");
              setImportError("");
            }}
            disabled={isImporting}
          >
            From File
          </Button>
          <Button
            variant={importMode === "link" ? "primary" : "standard"}
            onClick={() => {
              setImportMode("link");
              setImportError("");
            }}
            disabled={isImporting}
          >
            From Link
          </Button>
        </Flex>
        {importMode === "file" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={isImporting}
            />
            <Button
              variant="standard"
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              css={{ mb: "$4", alignSelf: "flex-start" }}
            >
              Choose File
            </Button>
          </>
        )}
        {importMode === "link" && (
          <Flex css={{ gap: "$4", mb: "$4" }} direction="column">
            <Input
              type="url"
              placeholder="https://example.com/templates.json"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              disabled={isImporting}
              css={{
                backgroundColor: "$surface_bright",
                border: "1px solid $border_default",
                color: "#191B23",
              }}
            />
            <Button
              variant="primary"
              onClick={handleImportFromLink}
              disabled={isImporting || !linkUrl.trim()}
              css={{ alignSelf: "flex-start" }}
            >
              Import
            </Button>
            <Text
              variant="caption"
              css={{ color: "#2E3038", mt: "$2", fontStyle: "italic" }}
            >
              Note: If the link doesn't work due to CORS error or any other issue,
              please download the file and upload it using the "From File" option.
            </Text>
          </Flex>
        )}
        {importError && (
          <Text
            variant="caption"
            css={{
              c: "$alert_error_default",
              mt: "$4",
              whiteSpace: "pre-line",
            }}
          >
            {importError}
          </Text>
        )}
      </Flex>

      {templates.length > 0 && (
        <Flex direction="column" css={{ gap: "$8" }}>
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onDelete={handleDeleteTemplate}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const TemplateCard = ({ template, onDelete }) => {
  const actions = useHMSActions();
  const localPeerRoleName = useHMSStore(selectLocalPeerRoleName);
  const { setWidgetState } = useWidgetState();
  const [error, setError] = useState("");

  const handleUseTemplate = async () => {
    try {
      const id = Date.now().toString();
      await actions.interactivityCenter.createPoll({
        id,
        title: template.title,
        anonymous: template.anonymous,
        rolesThatCanViewResponses: template.rolesThatCanViewResponses || [],
        type: template.type,
      });

      // Store template questions in widget state to be loaded in CreateQuestions
      setWidgetState({
        [WIDGET_STATE.pollInView]: id,
        [WIDGET_STATE.view]: WIDGET_VIEWS.CREATE_QUESTIONS,
        templateQuestions: template.questions,
      });
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  const questionCount = template.questions?.length || 0;
  const typeLabel = template.type === "quiz" ? "Quiz" : "Poll";

  return (
    <Flex
      direction="column"
      css={{
        backgroundColor: "$surface_bright",
        borderRadius: "$1",
        p: "$8",
      }}
    >
      <Text
        variant="sub1"
        css={{ color: "#191B23", fontWeight: "$semiBold", mb: "$4" }}
      >
        {template.title}
      </Text>
      <Text variant="body2" css={{ color: "#2E3038", mb: "$8" }}>
        {typeLabel} • {questionCount} question{questionCount !== 1 ? "s" : ""}
      </Text>
      {error && (
        <Text variant="caption" css={{ c: "$alert_error_default", mb: "$4" }}>
          {error}
        </Text>
      )}
      <Flex css={{ w: "100%", gap: "$4" }} justify="end">
        <Button
          variant="standard"
          onClick={() => onDelete(template.id)}
        >
          Delete
        </Button>
        <Button variant="primary" onClick={handleUseTemplate}>
          Use Template
        </Button>
      </Flex>
    </Flex>
  );
};
